// src/pages/About.js
// ──────────────────────────────────────────────────────────────────────────
// About Mbala Training Institute — real background, mission, vision,
// objectives and departments. Themed to match the rest of the site.
// ──────────────────────────────────────────────────────────────────────────

import React from 'react';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSiteContent } from '../context/SiteContentContext';
import theme from '../theme/theme';

const Header = styled.header`
  background: ${theme.color.blueDeep}; color: #fff;
  padding: 7rem ${theme.layout.gutter} 5rem;
  .inner { max-width: ${theme.layout.maxWidth}; margin: 0 auto; }
  h1 { font-weight: 400; font-size: clamp(2.4rem, 6vw, 4rem); color: #fff; max-width: 18ch; margin-top: 1.2rem; }
  p { margin-top: 1.2rem; max-width: 52ch; font-weight: 300; color: rgba(255,255,255,0.82); }
  .kicker { color: #fff; }
`;

const Section = styled.section`
  max-width: ${theme.layout.maxWidth}; margin: 0 auto; padding: 5rem ${theme.layout.gutter};
`;

const Two = styled.div`
  display: grid; grid-template-columns: 0.85fr 1.15fr; gap: 4rem; align-items: start;
  @media (max-width: 860px) { grid-template-columns: 1fr; gap: 2rem; }
  h2 { font-weight: 400; font-size: clamp(1.8rem, 4vw, 2.6rem); max-width: 14ch; }
  .lead { font-size: 1.05rem; color: ${theme.color.inkSoft}; margin: 0; }
`;

const MV = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin-top: 2rem;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
  .card { border: 1px solid ${theme.color.line}; border-left: 4px solid ${theme.color.lemon}; border-radius: ${theme.layout.radius}; padding: 1.6rem; background: #fff; }
  .card h4 { font-family: ${theme.font.body}; font-size: 0.72rem; letter-spacing: 0.16em; text-transform: uppercase; color: ${theme.color.accent}; margin-bottom: 0.6rem; }
  .card p { margin: 0; font-size: 0.95rem; color: ${theme.color.inkSoft}; }
`;

const Band = styled.section` background: ${theme.color.paperAlt}; border-block: 1px solid ${theme.color.line}; `;

const Objectives = styled.ol`
  list-style: none; counter-reset: o; padding: 0; margin: 2rem 0 0;
  max-width: 760px;
  li { counter-increment: o; position: relative; padding: 1rem 0 1rem 3rem; border-bottom: 1px solid ${theme.color.line}; color: ${theme.color.ink}; font-size: 1rem; }
  li::before { content: counter(o, decimal-leading-zero); position: absolute; left: 0; top: 1rem; font-family: ${theme.font.display}; color: ${theme.color.accent}; font-weight: 500; }
`;

const Depts = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem; margin-top: 2.5rem;
  @media (max-width: 720px) { grid-template-columns: 1fr; }
  .d { border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; padding: 1.6rem 1.8rem; background: #fff; }
  .d h3 { font-weight: 500; font-size: 1.3rem; color: ${theme.color.accent}; }
  .d p { margin: 0.5rem 0 0; font-size: 0.93rem; color: ${theme.color.inkSoft}; }
`;

const OBJECTIVES = [
  'Run demand-based TEVET programmes for unemployed youths, women and the general public.',
  'Deliver vocational skills training with a complete training-cycle approach.',
  'Enhance the employability of our graduates.',
  'Conduct labour-market related studies to keep training relevant.',
  'Prepare committed, dedicated subject experts as a prime national asset.',
  'Include and reach all deprived groups in our activities.',
  'Contribute to socioeconomic development through vocational training.',
];

const DEPTS = [
  { t: 'Agriculture', d: 'Crop production, animal husbandry, poultry and fish farming — taught as a business, not just a tradition.' },
  { t: 'Engineering', d: 'Building & plastering, carpentry & joinery, metal fabrication & welding, plumbing, electrical and tailoring.' },
  { t: 'Hospitality', d: 'Food production and professional, outdoor catering for events and enterprise.' },
  { t: 'ICT', d: 'Computer skills integrated across every trade for modern business.' },
];

// Core values — from the institute's 2024–2026 Strategic Plan.
const VALUES = [
  { t: 'Trust', d: 'In the spirit of working together, we trust every team member to do their part.' },
  { t: 'Goals', d: 'We keep a clear sight of where we are going at all times.' },
  { t: 'Teamwork', d: 'We work together for excellence.' },
  { t: 'Innovation', d: 'We strive for continuous improvement, finding better ways of doing things.' },
  { t: 'Ethics', d: 'In delivering training, we uphold high standards of integrity.' },
  { t: 'Clients', d: 'We value our association with every client, regardless of size or number.' },
];

const Values = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.4rem; margin-top: 2.5rem;
  @media (max-width: 760px) { grid-template-columns: 1fr 1fr; } @media (max-width: 460px) { grid-template-columns: 1fr; }
  .v { border: 1px solid ${theme.color.line}; border-top: 3px solid ${theme.color.lemon}; border-radius: ${theme.layout.radius}; padding: 1.4rem 1.5rem; background: #fff; }
  .v h3 { font-weight: 600; font-size: 1.1rem; color: ${theme.color.accent}; }
  .v p { margin: 0.4rem 0 0; font-size: 0.9rem; color: ${theme.color.inkSoft}; }
`;

// Leadership — Director featured, then the rest. Add a `photo` path to show a
// real portrait (e.g. photo: '/team/director.jpg' with the file in public/team/);
// leave '' to show a styled initial placeholder.
const DIRECTOR = {
  role: 'Managing Partner', name: 'Chipungu G. Simpungwe', photo: '',
  bio: 'It is my expectation that this institute will facilitate quality and affordable training with flexible delivery — shaping learners from a raw, theoretical state into skilled professionals who become tools for national development.',
};
const LEADERS = [
  { role: 'Principal', name: 'Name to be announced', photo: '' },
  { role: 'Registrar', name: 'Name to be announced', photo: '' },
  { role: 'Training Manager', name: 'Name to be announced', photo: '' },
  { role: 'HOD — Agriculture', name: 'Name to be announced', photo: '' },
  { role: 'HOD — Engineering', name: 'Name to be announced', photo: '' },
  { role: 'HOD — Hospitality', name: 'Name to be announced', photo: '' },
  { role: 'HOD — ICT', name: 'Name to be announced', photo: '' },
  { role: 'Student Affairs Officer', name: 'Name to be announced', photo: '' },
];
const initial = (r) => r.replace('HOD — ', '').trim()[0].toUpperCase();

const DirectorWrap = styled.div`
  display: grid; grid-template-columns: 0.8fr 1.2fr; gap: 3rem; align-items: center; margin-top: 2.5rem;
  @media (max-width: 760px) { grid-template-columns: 1fr; gap: 1.8rem; }
`;
const Portrait = styled.div`
  aspect-ratio: 4/5; border-radius: ${theme.layout.radius}; overflow: hidden; position: relative;
  background: linear-gradient(160deg, ${theme.color.accentDark} 0%, ${theme.color.accent} 100%);
  display: grid; place-items: center;
  img { width: 100%; height: 100%; object-fit: cover; }
  .ph { font-family: ${theme.font.display}; font-weight: 700; color: rgba(255,255,255,0.95); }
  &::after { content: ''; position: absolute; left: 0; bottom: 0; height: 5px; width: 100%; background: ${theme.gradient.rule}; }
`;
const DirInfo = styled.div`
  .role { color: ${theme.color.accent}; font-weight: 700; font-size: 0.78rem; letter-spacing: 0.16em; text-transform: uppercase; }
  h3 { font-weight: 400; font-size: clamp(1.8rem, 4vw, 2.6rem); margin: 0.5rem 0; }
  p { color: ${theme.color.inkSoft}; font-size: 1rem; max-width: 46ch; }
`;
const LeaderGrid = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.6rem; margin-top: 3rem;
  @media (max-width: 820px) { grid-template-columns: repeat(2, 1fr); } @media (max-width: 460px) { grid-template-columns: 1fr; }
  .role { font-weight: 600; color: ${theme.color.ink}; margin-top: 0.8rem; }
  .name { font-size: 0.84rem; color: ${theme.color.inkSoft}; margin-top: 0.15rem; }
`;

function About() {
  const intro = useSiteContent('intro');
  const leadership = useSiteContent('leadership') || {};
  const editableValues = useSiteContent('values');
  const VALUES_LIST = (Array.isArray(editableValues) && editableValues.length) ? editableValues : VALUES;
  const director = leadership.director || DIRECTOR;
  const leaders = (Array.isArray(leadership.leaders) && leadership.leaders.length) ? leadership.leaders : LEADERS;

  return (
    <>
      <Navbar />

      <Header>
        <div className="inner">
          <span className="kicker">About us</span>
          <h1>A community skills hub in Mbala.</h1>
          <p>
            Mbala Training Institute is a registered, community-based TEVET facility at Ronald Penza,
            about 1.8&nbsp;km from Mbala town — empowering youths, women and the general public with
            practical, life-purpose skills.
          </p>
        </div>
      </Header>

      <Section>
        <Two>
          <div><span className="kicker">Our purpose</span><h2>Skills are a means to a dignified life.</h2></div>
          <div>
            <p className="lead">{intro?.purpose}</p>
            <MV>
              <div className="card"><h4>Mission</h4><p>{intro?.mission}</p></div>
              <div className="card"><h4>Vision</h4><p>{intro?.vision}</p></div>
            </MV>
          </div>
        </Two>
      </Section>

      <Band>
        <Section>
          <span className="kicker">What we set out to do</span>
          <h2 style={{ fontWeight: 400, fontSize: 'clamp(1.8rem,4vw,2.6rem)', marginTop: '1rem' }}>Our objectives</h2>
          <Objectives>
            {OBJECTIVES.map((o) => <li key={o}>{o}</li>)}
          </Objectives>
        </Section>
      </Band>

      <Section>
        <span className="kicker">Four departments</span>
        <h2 style={{ fontWeight: 400, fontSize: 'clamp(1.8rem,4vw,2.6rem)', marginTop: '1rem' }}>What we teach</h2>
        <Depts>
          {DEPTS.map((d) => <div className="d" key={d.t}><h3>{d.t}</h3><p>{d.d}</p></div>)}
        </Depts>
      </Section>

      <Band>
        <Section>
          <span className="kicker">What we stand for</span>
          <h2 style={{ fontWeight: 400, fontSize: 'clamp(1.8rem,4vw,2.6rem)', marginTop: '1rem' }}>Our values</h2>
          <Values>
            {VALUES_LIST.map((v) => <div className="v" key={v.t}><h3>{v.t}</h3><p>{v.d}</p></div>)}
          </Values>
        </Section>
      </Band>

      <Band>
        <Section>
          <span className="kicker">Our team</span>
          <h2 style={{ fontWeight: 400, fontSize: 'clamp(1.8rem,4vw,2.6rem)', marginTop: '1rem' }}>Leadership</h2>

          <DirectorWrap>
            <Portrait>
              {director.photo
                ? <img src={director.photo} alt={director.role} />
                : <span className="ph" style={{ fontSize: '4rem' }}>{initial(director.role)}</span>}
            </Portrait>
            <DirInfo>
              <div className="role">{director.role}</div>
              <h3>{director.name}</h3>
              <p>{director.bio}</p>
            </DirInfo>
          </DirectorWrap>

          <LeaderGrid>
            {leaders.map((m) => (
              <div key={m.role}>
                <Portrait>
                  {m.photo
                    ? <img src={m.photo} alt={m.role} />
                    : <span className="ph" style={{ fontSize: '2.2rem' }}>{initial(m.role)}</span>}
                </Portrait>
                <div className="role">{m.role}</div>
                <div className="name">{m.name}</div>
              </div>
            ))}
          </LeaderGrid>
        </Section>
      </Band>

      <Footer />
    </>
  );
}

export default About;
